/**
 * Quarterly + annual + TTM fundamentals series, generated deterministically per
 * symbol so the UI looks alive without a backend. In production these come from
 * an NGX X-Issuer Portal feed + Bloomberg/FactSet equivalents — the IO shape is
 * `(symbol, aggregation) -> MetricSeries[]` so the swap is a one-line change.
 */

export type Aggregation = 'Quarterly' | 'Annual' | 'TTM';

export interface MetricSeries {
  name: string;
  values: number[];        // most-recent last
  labels: string[];        // x-axis ticks (e.g. ['Q4 2024','Q1 2025',...])
  format: 'plain' | 'currency' | 'percent' | 'ratio';
  /** Optional thicker formatting hint — kept for future per-metric overrides. */
}

const METRICS: ReadonlyArray<{ name: string; format: MetricSeries['format']; baseFn: (s: number) => number; drift?: number }> = [
  { name: 'Earnings per Share TTM',  format: 'currency', baseFn: (s) => 1 + (s % 50) / 10,   drift: -0.05 },
  { name: 'Dividends per Share TTM', format: 'currency', baseFn: (s) => 0.5 + (s % 30) / 50, drift: -0.02 },
  { name: 'Book Value per Share',    format: 'currency', baseFn: (s) => 30 + (s % 60),       drift: -1.2 },
  { name: 'Cash Flow per Share',     format: 'currency', baseFn: (s) => 5 + (s % 20) / 4,    drift: -0.15 },
  { name: 'Free Cash Flow per Share',format: 'currency', baseFn: (s) => 2 + (s % 15) / 4,    drift: 0.08 },

  { name: 'Sales per Share',         format: 'currency', baseFn: (s) => 15 + (s % 30),       drift: -0.7 },
  { name: 'Return on Equity',        format: 'percent',  baseFn: (s) => 12 + (s % 20),       drift: 0.5 },
  { name: 'Return on Assets',        format: 'percent',  baseFn: (s) => 3 + (s % 8),         drift: -0.12 },
  { name: 'Gross Profit Margin',     format: 'percent',  baseFn: (s) => 30 + (s % 25),       drift: -0.85 },
  { name: 'Operating Profit Margin', format: 'percent',  baseFn: (s) => 18 + (s % 15),       drift: 0.5 },

  { name: 'Tax Rate',                format: 'percent',  baseFn: (s) => 14 + (s % 12),       drift: 0.4 },
  { name: 'Net Profit Margin',       format: 'percent',  baseFn: (s) => 12 + (s % 12),       drift: 0.3 },
  { name: 'Total Asset Turnover',    format: 'ratio',    baseFn: (s) => 1 + (s % 9) / 10,    drift: -0.03 },
  { name: 'Inventory Turnover',      format: 'ratio',    baseFn: (s) => 30 + (s % 40),       drift: 1 },
  { name: 'Long-term Debt to Capital', format: 'percent', baseFn: (s) => 30 + (s % 30),      drift: -1 },

  { name: 'Financial Leverage',      format: 'ratio',    baseFn: (s) => 3 + (s % 6) / 2,     drift: 0.1 },
  { name: 'Dividend Payout',         format: 'plain',    baseFn: (s) => 5 + (s % 7),         drift: -0.2 },
  { name: 'Quick Ratio',             format: 'ratio',    baseFn: (s) => 0.4 + (s % 5) / 10,  drift: -0.01 },
  { name: 'Current Ratio',           format: 'ratio',    baseFn: (s) => 1.2 + (s % 8) / 10,  drift: -0.04 },
];

function seed(s: string, salt: string): number {
  let h = 5381; const all = s + '|' + salt;
  for (let i = 0; i < all.length; i++) h = ((h << 5) + h) + all.charCodeAt(i);
  return Math.abs(h);
}

function periodLabels(agg: Aggregation): string[] {
  const now = new Date();
  const y = now.getUTCFullYear(); const m = now.getUTCMonth();
  if (agg === 'Annual') {
    return [y - 4, y - 3, y - 2, y - 1, y].map((yr) => String(yr));
  }
  if (agg === 'TTM') {
    return ['T-4', 'T-3', 'T-2', 'T-1', 'TTM'];
  }
  // Quarterly — produce 5 most-recent quarters with year suffix.
  const out: string[] = [];
  let qy = y, qm = m;
  for (let i = 0; i < 5; i++) {
    const q = Math.floor(qm / 3) + 1;
    out.unshift(`Q${q} ${qy}`);
    qm -= 3; if (qm < 0) { qm += 12; qy -= 1; }
  }
  return out;
}

export function fundamentalsRichFor(symbol: string, agg: Aggregation): MetricSeries[] {
  const labels = periodLabels(agg);
  return METRICS.map(({ name, format, baseFn, drift = 0 }) => {
    const s = seed(symbol, name);
    const base = baseFn(s);
    const wobble = (i: number) => 1 + ((seed(symbol, name + 'w' + i) % 200) - 100) / 4000;
    // Generate 5 values with a small consistent drift across the window plus per-period wobble.
    const values = labels.map((_, i) => +((base + drift * (4 - i)) * wobble(i)).toFixed(2));
    return { name, values, labels, format };
  });
}

const COMPANY_DESC: Record<string, string> = {
  ZENITHBANK: 'Zenith Bank is a leading Nigerian financial institution offering retail, corporate, and investment banking services. The company provides savings and current accounts, loans, trade finance, treasury services, and digital banking to consumers and SMEs across West Africa.',
  DANGCEM: 'Dangote Cement is sub-Saharan Africa\u2019s largest cement producer, with integrated plants and grinding terminals across Nigeria and 9 other African countries. The company manufactures and distributes cement, ready-mix concrete, and clinker.',
  MTNN: 'MTN Nigeria is the country\u2019s largest mobile network operator by subscribers, providing voice, SMS, mobile data, and digital services to consumers, enterprises, and the public sector.',
  GTCO: 'Guaranty Trust Holding is the holding company of GTBank Nigeria and several West African subsidiaries, offering retail and corporate banking, asset management, and pension administration.',
  SEPLAT: 'Seplat Energy is an indigenous Nigerian upstream oil and gas company with assets in the Niger Delta and a growing focus on natural-gas processing and power generation.',
  AIRTELAFRI: 'Airtel Africa provides telecommunications and mobile-money services across 14 countries in sub-Saharan Africa, with Nigeria as its largest market by revenue.',
  BUACEMENT: 'BUA Cement is one of Nigeria\u2019s largest cement producers, with integrated plants in Edo and Sokoto serving Nigeria, Niger, and parts of West Africa.',
};

export function companyDescription(symbol: string): string {
  return COMPANY_DESC[symbol] ?? `${symbol} is a Nigerian Stock Exchange listed equity. Detailed company description, business segments, and regulatory filings will be sourced from the NGX X-Issuer Portal in production.`;
}

export function formatMetricValue(v: number, format: MetricSeries['format']): string {
  switch (format) {
    case 'currency': return v.toFixed(2);
    case 'percent':  return v.toFixed(2);
    case 'ratio':    return v.toFixed(2);
    default:         return v.toFixed(2);
  }
}
